/* assets/story-rotor.js
 * Product-card "Instagram Stories" style image rotator (hover-only)
 * - Plays only while the user's cursor is over the card
 * - Resets to the first image on mouse leave
 * - Cross-fade between frames, thin progress bars at the top
 * - Respects prefers-reduced-motion
 * Requires markup:
 *   <div class="story-rotor" data-frames="url1,url2,..." data-count="N">
 *     <div class="story-bars"></div>
 *     <div class="story-img-wrap">
 *       <img class="story-img" src="featured.jpg" />
 *     </div>
 *   </div>
 */

(() => {
  'use strict';

  // Respect users who prefer reduced motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const DURATION = 5000; // ms per frame

  // Build the little "story" bars
  function makeBars(container, count) {
    const bars = container.querySelector('.story-bars');
    if (!bars) return [];
    bars.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const span = document.createElement('span');
      const fill = document.createElement('i');
      span.appendChild(fill);
      bars.appendChild(span);
    }
    return Array.from(bars.querySelectorAll('i'));
  }

  // Cross-fade to next image without layout shift
  function crossFade(imgWrap, currentImg, nextSrc) {
    const ghost = currentImg.cloneNode();
    ghost.src = nextSrc;
    ghost.classList.add('story-ghost');
    imgWrap.appendChild(ghost);
    // next frame → fade in
    requestAnimationFrame(() => { ghost.style.opacity = 1; });
    // after fade completes → swap and clean up
    setTimeout(() => {
      currentImg.src = nextSrc;
      ghost.remove();
    }, 380);
  }

  // Initialize all story rotors on the page
  document.querySelectorAll('.story-rotor').forEach(card => {
    const dataAttr = card.getAttribute('data-frames') || '';
    const frames = dataAttr.split(',').map(s => s.trim()).filter(Boolean);
    if (frames.length < 2) return; // nothing to rotate

    const imgWrap = card.querySelector('.story-img-wrap');
    const img = card.querySelector('.story-img');
    if (!imgWrap || !img) return;

    const fills = makeBars(card, frames.length);

    let frameIdx = 0;      // current frame index
    let rafId = null;      // requestAnimationFrame handle
    let startTS = 0;       // animation start timestamp

    // Update the visual progress bars
    function setProgress(i, p) { // p in [0..1]
      fills.forEach((fill, k) => {
        if (!fill) return;
        if (k < i) {
          fill.style.inset = '0 0 0 0';
        } else if (k > i) {
          fill.style.inset = '0 100% 0 0';
        } else {
          const right = Math.max(0, 100 - p * 100);
          fill.style.inset = `0 ${right}% 0 0`;
        }
      });
    }

    // Animation loop for the current frame
    function step(ts) {
      if (!startTS) startTS = ts;
      const elapsed = ts - startTS;
      const p = Math.min(1, elapsed / DURATION);
      setProgress(frameIdx, p);

      if (elapsed >= DURATION) {
        frameIdx = (frameIdx + 1) % frames.length;
        startTS = ts;
        crossFade(imgWrap, img, frames[frameIdx]);
      }
      rafId = requestAnimationFrame(step);
    }

    function start() {
      if (rafId) return;
      startTS = 0;
      rafId = requestAnimationFrame(step);
    }

    function stop() {
      if (!rafId) return;
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    function resetToFirst() {
      frameIdx = 0;
      setProgress(0, 0);
      if (img.src !== frames[0]) img.src = frames[0];
    }

    // --- Hover-only behavior ---
    card.addEventListener('mouseenter', () => {
      // begin playing from the start each time hover begins
      resetToFirst();
      start();
    });

    card.addEventListener('mouseleave', () => {
      // stop immediately and reset to frame 0
      stop();
      resetToFirst();
    });

    // Optional: if the image fails to load, skip that frame gracefully
    img.addEventListener('error', () => {
      // try to move to the next frame on error
      frameIdx = (frameIdx + 1) % frames.length;
      img.src = frames[frameIdx];
    });

    // On initial render (no hover), keep bars at 0 and featured image shown
    resetToFirst();
  });
})();
